import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Save, BookOpen, Eye, TrendingUp, Mail, Bell, Trash2, Heart, Pencil } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [grandma, setGrandma] = useState(null);
  const [stories, setStories] = useState([]);
  const [favoriteStories, setFavoriteStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    photo_url: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [grandmaRes, storiesRes, notifRes, favRes] = await Promise.all([
        axios.get(`${API}/grandmas/me`, { headers }),
        axios.get(`${API}/stories/my`, { headers }),
        axios.get(`${API}/users/me/notifications`, { headers }).catch(() => ({ data: { email_notifications: true } })),
        axios.get(`${API}/favorites`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      if (grandmaRes.data) {
        setGrandma(grandmaRes.data);
        setFormData({
          name: grandmaRes.data.name || '',
          bio: grandmaRes.data.bio || '',
          photo_url: grandmaRes.data.photo_url || '',
        });
      }
      setStories(storiesRes.data || []);
      setEmailNotifications(notifRes.data?.email_notifications !== false);

      // Fetch full story data for favorites
      const favIds = favRes.data || [];
      if (favIds.length > 0) {
        const allStories = await axios.get(`${API}/stories?approved_only=true`);
        setFavoriteStories(allStories.data.filter(s => favIds.includes(s.id)));
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (grandma) {
        // Update existing profile
        const response = await axios.put(`${API}/grandmas/${grandma.id}`, formData);
        setGrandma(response.data);
        toast.success(t('profile.updated'));
      } else {
        // Create new profile
        const response = await axios.post(`${API}/grandmas`, formData);
        setGrandma(response.data);
        toast.success(t('profile.created'));
      }
    } catch (error) {
      toast.error(t('profile.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData({ ...formData, photo_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}` });
      toast.success(t('profile.photo_uploaded'));
    } catch (error) {
      toast.error(t('profile.photo_error'));
    }
  };

  const handleToggleEmailNotifications = async (checked) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/users/me/notifications`, 
        { email_notifications: checked },
        { headers }
      );
      setEmailNotifications(checked);
      toast.success(checked ? t('profile.email_on') : t('profile.email_off'));
    } catch (error) {
      toast.error(t('profile.email_error'));
    }
  };

  const handleDeleteStory = async () => {
    if (!storyToDelete) return;
    
    setDeleting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/stories/${storyToDelete.id}`, { headers });
      setStories(stories.filter(s => s.id !== storyToDelete.id));
      toast.success(t('profile.deleted'));
      setDeleteDialogOpen(false);
      setStoryToDelete(null);
    } catch (error) {
      toast.error(t('profile.delete_error'));
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (story) => {
    setStoryToDelete(story);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="profile-loading">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="w-32 h-32 rounded-full mx-auto" />
          <Skeleton className="w-48 h-8 mx-auto" />
          <Skeleton className="w-full h-40" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-2 border-border shadow-xl mb-8">
            <CardHeader className="text-center">
              <CardTitle className="font-display text-3xl">
                {grandma ? t('profile.title') : t('profile.create')}
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSave} className="space-y-6">
                {/* Avatar */}
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <Avatar className="w-32 h-32 border-4 border-gold/30">
                      <AvatarImage src={formData.photo_url} alt={formData.name} />
                      <AvatarFallback className="bg-old-rose text-night-blue text-3xl font-display">
                        {formData.name?.charAt(0) || '👵'}
                      </AvatarFallback>
                    </Avatar>
                    <label 
                      htmlFor="photo-upload"
                      className="absolute bottom-0 right-0 p-2 bg-gold rounded-full cursor-pointer hover:bg-gold/80 transition-colors"
                    >
                      <Camera className="w-4 h-4 text-night-blue" />
                    </label>
                    <input
                      id="photo-upload"
                      data-testid="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground font-accent">
                    {t('profile.upload_hint')}
                  </p>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-accent">{t('profile.name')}</Label>
                  <Input
                    id="name"
                    data-testid="profile-name"
                    type="text"
                    placeholder={t('profile.name_placeholder')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="text-lg"
                  />
                </div>

                {/* Bio */}
                <div className="space-y-2">
                  <Label htmlFor="bio" className="font-accent">{t('profile.bio')}</Label>
                  <Textarea
                    id="bio"
                    data-testid="profile-bio"
                    placeholder={t('profile.bio_placeholder')}
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    required
                    rows={4}
                    className="resize-none"
                  />
                </div>

                {/* Photo URL (alternative) */}
                <div className="space-y-2">
                  <Label htmlFor="photo_url" className="font-accent">{t('profile.photo_url')}</Label>
                  <Input
                    id="photo_url"
                    data-testid="profile-photo-url"
                    type="url"
                    placeholder="https://..."
                    value={formData.photo_url}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="save-profile"
                  disabled={saving}
                  className="w-full rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('profile.save')}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Email Notification Settings */}
          <Card className="border-2 border-border shadow-xl mb-8">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Bell className="w-5 h-5 text-gold" />
                {t('profile.notif_title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t('profile.email_notif')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('profile.email_notif_desc')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={emailNotifications}
                  onCheckedChange={handleToggleEmailNotifications}
                  data-testid="email-notifications-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Favorites Section */}
          {favoriteStories.length > 0 && (
            <Card className="border-2 border-border shadow-xl mb-8">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-400 fill-red-400" />
                  {t('profile.favorites')} ({favoriteStories.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {favoriteStories.map((story) => (
                    <div
                      key={story.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/story/${story.id}`)}
                      data-testid={`favorite-story-${story.id}`}
                    >
                      {story.cover_image_url ? (
                        <div 
                          className="w-12 h-16 rounded bg-cover bg-center flex-shrink-0"
                          style={{ backgroundImage: `url(${story.cover_image_url})` }}
                        />
                      ) : (
                        <div className="w-12 h-16 rounded bg-gold/20 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-5 h-5 text-gold" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm">{story.title}</h3>
                        <p className="text-xs text-muted-foreground">{t('misc.by')} {story.grandma_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* My Stories with Stats */}
          {stories.length > 0 && (
            <div className="space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="text-2xl font-semibold">{stories.length}</p>
                        <p className="text-sm text-muted-foreground">{t('profile.stories_count')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-semibold">
                          {stories.reduce((sum, s) => sum + (s.read_count || 0), 0).toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground">{t('profile.read_count')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm col-span-2 md:col-span-1">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-semibold">
                          {stories.filter(s => s.is_approved).length}
                        </p>
                        <p className="text-sm text-muted-foreground">{t('profile.published')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stories List */}
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-4">{t('profile.my_stories')}</h2>
                <div className="space-y-3">
                  {stories.map((story) => (
                    <Card key={story.id} className="border-0 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                      <div className="flex items-center">
                        {story.cover_image_url && (
                          <div 
                            className="w-20 h-20 bg-cover bg-center flex-shrink-0"
                            style={{ backgroundImage: `url(${story.cover_image_url})` }}
                          />
                        )}
                        <CardContent className="flex-1 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium">{story.title}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-1">{story.description}</p>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground ml-2">
                              {!story.is_approved && (
                                <span className="px-2 py-1 bg-gold/10 text-gold rounded-full text-xs font-medium">
                                  {t('profile.waiting')}
                                </span>
                              )}
                              <div className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                <span className="font-medium">{story.read_count || 0}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/upload?edit=${story.id}`)}
                                className="h-8 w-8 p-0 text-gold hover:text-gold hover:bg-gold/10"
                                data-testid={`edit-story-${story.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(story)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={`delete-story-${story.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{t('profile.delete_title')}</DialogTitle>
                <DialogDescription className="text-base">
                  {t('profile.delete_confirm').replace('{title}', storyToDelete?.title || '')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={deleting}
                >
                  {t('profile.delete_cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteStory}
                  disabled={deleting}
                  data-testid="confirm-delete-story"
                >
                  {deleting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('profile.delete_btn')}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;
